import { forwardRef, HTMLAttributes, Ref } from 'react';

import { IBaseChartProps, IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { AreaChart, IAreaChartProps } from './AreaChart/AreaChart';
import { BarChart, IBarChartProps } from './BarChart/BarChart';
import { chartStylesDefault, IChartClassNames, IChartStyles } from './Chart.styles';
import { FunnelChart, IFunnelChartProps } from './FunnelChart/FunnelChart';
import { ILineChartProps, LineChart } from './LineChart/LineChart';
import { IPieChartProps, PieChart } from './PieChart/PieChart';
import { IRadialChartProps, RadialChart } from './RadialChart/RadialChart';
import { IRoseChartProps, RoseChart } from './RoseChart/RoseChart';

export enum ChartType {
  Bar = 'Bar',
  Pie = 'Pie',
  Line = 'Line',
  Area = 'Area',
  Rose = 'Rose',
  Radial = 'Radial',
  Funnel = 'Funnel',
}

const render = (
  data: { [key: string]: any }[],
  labelFieldName: string,
  valueFieldNames: string[],
  chartType: ChartType,
  chartProps?:
    | IAreaChartProps
    | IBarChartProps
    | IFunnelChartProps
    | ILineChartProps
    | IPieChartProps
    | IRadialChartProps
    | IRoseChartProps
) => {
  const baseProps: IBarChartProps = { data, labelFieldName, valueFieldNames };
  switch (chartType) {
    case ChartType.Bar:
      return <BarChart {...baseProps} {...chartProps} />;
    case ChartType.Pie:
      return <PieChart {...baseProps} {...chartProps} />;
    case ChartType.Line:
      return <LineChart {...baseProps} {...chartProps} />;
    case ChartType.Area:
      return <AreaChart {...baseProps} {...chartProps} />;
    case ChartType.Rose:
      return <RoseChart {...baseProps} {...chartProps} />;
    case ChartType.Radial:
      return <RadialChart {...baseProps} {...chartProps} />;
    case ChartType.Funnel:
      return <FunnelChart {...baseProps} {...chartProps} />;
  }
};

export interface IChartProps extends IComponent<IChartStyles>, HTMLAttributes<HTMLDivElement>, IBaseChartProps {
  chartType?: ChartType;
  chartProps?:
    | IAreaChartProps
    | IBarChartProps
    | IFunnelChartProps
    | ILineChartProps
    | IPieChartProps
    | IRadialChartProps
    | IRoseChartProps;
}

export const Chart = forwardRef((props: IChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const {
    className,
    styles,
    data = [],
    chartType = ChartType.Bar,
    labelFieldName = '',
    valueFieldNames = [],
    chartProps,
    ...elementProps
  } = props;
  const classNames = useClassNames<Partial<IChartStyles>, IChartClassNames>(
    'Chart',
    chartStylesDefault,
    styles,
    className
  );
  return (
    <div {...elementProps} ref={ref} className={classNames.root}>
      {render(data, labelFieldName, valueFieldNames, chartType, chartProps)}
    </div>
  );
});
